"use client";

import { useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import React from "react";
import { z } from "zod";
import { cn } from "@/lib/utils";

// Form validation schema
export const loginFormSchema = z.object({
	email: z.string().email("Invalid email address 😟"),
	password: z.string().min(8, {
		message: "Password must be at least 8 characters long",
	}),
});

// Simple Input with floating label
export const Input = React.forwardRef<
	HTMLInputElement,
	React.InputHTMLAttributes<HTMLInputElement> & {
		label: string;
		error?: boolean;
	}
>(({ label, error, className, ...props }, ref) => {
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	// Combine the forwarded ref with our internal ref
	React.useImperativeHandle(ref, () => inputRef.current!);

	return (
		<div className="relative mb-6">
			<label
				className={cn(
					"block mb-2 text-sm font-medium transition-colors duration-200",
					isFocused
						? "text-primary"
						: error
						? "text-destructive"
						: "text-muted-foreground"
				)}
			>
				{label}
			</label>
			<div className="relative">
				<input
					ref={inputRef}
					{...props}
					onFocus={(e) => {
						setIsFocused(true);
						props.onFocus?.(e);
					}}
					onBlur={(e) => {
						setIsFocused(false);
						props.onBlur?.(e);
					}}
					className={cn(
						"relative w-full px-4 py-3 bg-white rounded-xl",
						"border border-[#e5e7eb]",
						"transition-all duration-200 ease-in-out",
						"focus:outline-none",
						"text-foreground",
						"[&:-webkit-autofill]:bg-white",
						"[&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset]",
						isFocused
							? "border-primary/50 ring-1 ring-primary/20"
							: error
							? "border-destructive"
							: "hover:border-primary/30",
						className
					)}
				/>
			</div>
		</div>
	);
});
Input.displayName = "Input";

// Magnetic Button Component
export const MagneticButton = React.forwardRef<
	HTMLButtonElement,
	React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }
>(({ children, loading, className, ...props }, ref) => {
	const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
	const [isHovered, setIsHovered] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleMouseMove = (e: React.MouseEvent) => {
		if (!buttonRef.current) return;

		const rect = buttonRef.current.getBoundingClientRect();
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;

		const deltaX = (e.clientX - centerX) * 0.15;
		const deltaY = (e.clientY - centerY) * 0.15;

		setMousePosition({ x: deltaX, y: deltaY });
	};

	// Merge refs
	React.useImperativeHandle(ref, () => buttonRef.current!);

	return (
		<button
			ref={buttonRef}
			onMouseMove={handleMouseMove}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => {
				setMousePosition({ x: 0, y: 0 });
				setIsHovered(false);
			}}
			disabled={loading || props.disabled}
			className={cn(
				"relative w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium",
				"transition-all duration-300 ease-out",
				"hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
				"disabled:opacity-50 disabled:cursor-not-allowed",
				isHovered ? "shadow-xl scale-[1.02]" : "shadow-lg",
				className
			)}
			style={{
				transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
			}}
			{...props}
		>
			{loading ? (
				<div className="flex items-center justify-center">
					<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					Signing in...
				</div>
			) : (
				children
			)}
		</button>
	);
});
MagneticButton.displayName = "MagneticButton";

// Animated Grid component
export const AnimatedGrid = () => (
	<div className="fixed inset-0 overflow-hidden pointer-events-none bg-white">
		<div 
			className="absolute inset-0 opacity-[0.15] w-[200%] h-[200%]"
			style={{
				backgroundImage: `
					linear-gradient(rgba(148,163,184,0.3) 1px, transparent 1px),
					linear-gradient(90deg, rgba(148,163,184,0.3) 1px, transparent 1px)
				`,
				backgroundSize: '50px 50px',
				animation: 'gridMove 20s linear infinite'
			}}
		/>
	</div>
);

// Animation styles
export const loginAnimationStyles = `
	@keyframes gridMove {
		0% { transform: translate(0, 0); }
		100% { transform: translate(-50px, -50px); }
	}

	.animate-fade-in {
		animation: fadeIn 0.6s ease-out forwards;
	}

	.animate-fade-in-delay {
		animation: fadeIn 0.6s ease-out 0.2s forwards;
		opacity: 0;
	}

	.animate-fade-in-delay-2 {
		animation: fadeIn 0.6s ease-out 0.4s forwards;
		opacity: 0;
	}

	.animate-fade-in-delay-3 {
		animation: fadeIn 0.6s ease-out 0.6s forwards;
		opacity: 0;
	}

	.animate-slide-up {
		animation: slideUp 0.6s ease-out forwards;
	}

	.animate-slide-up-delay {
		animation: slideUp 0.6s ease-out 0.2s forwards;
		opacity: 0;
	}

	.animate-slide-up-delay-2 {
		animation: slideUp 0.6s ease-out 0.4s forwards;
		opacity: 0;
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
`;
